import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, QrCode, CheckCircle2, LogOut, Send, Loader2, Users, Check, Phone } from 'lucide-react';
import toast from 'react-hot-toast';

const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

const WhatsAppSetup = () => {
  const [status, setStatus] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [testForm, setTestForm] = useState({ number: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const pollRef = useRef(null);

  const [groups, setGroups] = useState(null);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [defaultGroup, setDefaultGroup] = useState(null);
  const [savingGroupId, setSavingGroupId] = useState(null);

  const [alertNumber, setAlertNumber] = useState('');
  const [isSavingAlertNumber, setIsSavingAlertNumber] = useState(false);

  const [pairNumber, setPairNumber] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);

  const handleRequestPairingCode = async (e) => {
    e.preventDefault();
    if (!pairNumber) return;
    setIsRequestingCode(true);
    try {
      const res = await fetch('/api/whatsapp/pairing-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ number: pairNumber })
      });
      const json = await res.json();
      if (json.code) setPairingCode(json.code);
      else toast.error(json.error || 'Could not get a pairing code');
    } catch (err) {
      toast.error(err.message);
    }
    setIsRequestingCode(false);
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/whatsapp/status', { headers: authHeaders() });
      const json = await res.json();
      if (!json.error) setStatus(json);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDefaultGroup = async () => {
    try {
      const res = await fetch('/api/whatsapp/default-group', { headers: authHeaders() });
      const json = await res.json();
      if (!json.error) setDefaultGroup(json.id ? json : null);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const res = await fetch('/api/whatsapp/groups', { headers: authHeaders() });
      const json = await res.json();
      if (Array.isArray(json)) setGroups(json);
      else toast.error(json.error || 'Failed to load groups');
    } catch (err) {
      toast.error(err.message);
    }
    setIsLoadingGroups(false);
  };

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollRef.current);
  }, []);

  const fetchAlertNumber = async () => {
    try {
      const res = await fetch('/api/whatsapp/alert-number', { headers: authHeaders() });
      const json = await res.json();
      if (!json.error) setAlertNumber(json.number || '');
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (status?.status === 'connected') {
      fetchDefaultGroup();
      fetchAlertNumber();
      if (!groups) fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status]);

  const handleSaveAlertNumber = async (e) => {
    e.preventDefault();
    if (!alertNumber) return;
    setIsSavingAlertNumber(true);
    try {
      const res = await fetch('/api/whatsapp/alert-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ number: alertNumber })
      });
      const json = await res.json();
      if (json.status === 'success') toast.success('Alert number saved');
      else toast.error(json.error || 'Failed to save');
    } catch (err) {
      toast.error(err.message);
    }
    setIsSavingAlertNumber(false);
  };

  const handleSetDefaultGroup = async (group) => {
    setSavingGroupId(group.id);
    try {
      const res = await fetch('/api/whatsapp/default-group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ id: group.id, name: group.name })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setDefaultGroup(group);
        toast.success(`"${group.name.trim()}" set as the default report group`);
      } else {
        toast.error(json.error || 'Failed to save');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setSavingGroupId(null);
  };

  const handleLogout = async () => {
    if (!window.confirm('Disconnect WhatsApp? You will need to scan a new QR code to pair again.')) return;
    setIsLoggingOut(true);
    try {
      await fetch('/api/whatsapp/logout', { method: 'POST', headers: authHeaders() });
      toast.success('Disconnected. Waiting for a new QR code...');
      fetchStatus();
    } catch (err) {
      toast.error(err.message);
    }
    setIsLoggingOut(false);
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testForm.number || !testForm.message) return;
    setIsSending(true);
    try {
      const res = await fetch('/api/whatsapp/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(testForm)
      });
      const json = await res.json();
      if (json.status === 'success') {
        toast.success('Message sent');
        setTestForm({ number: '', message: '' });
      } else {
        toast.error(json.error || 'Failed to send message');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setIsSending(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-16">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-3xl p-8 text-white shadow-lg">
        <h1 className="text-3xl font-black mb-2 flex items-center gap-3">
          <MessageCircle size={32} /> WhatsApp
        </h1>
        <p className="text-emerald-100 font-medium text-lg">Pair a WhatsApp number so the app can send messages directly.</p>
      </div>

      <div className="glass-panel p-6 space-y-4">
        {!status ? (
          <div className="text-center text-slate-500 py-6 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading status...
          </div>
        ) : status.status === 'connected' ? (
          <div className="flex flex-col items-center text-center py-6 gap-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <div>
              <p className="font-bold text-slate-800 text-lg">Connected</p>
              <p className="text-slate-500 text-sm">Paired as +{status.connectedNumber || 'unknown number'}</p>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 font-medium text-sm disabled:opacity-50"
            >
              <LogOut className="w-4 h-4" /> {isLoggingOut ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        ) : status.status === 'qr' && status.qr ? (
          <div className="flex flex-col items-center text-center py-4 gap-3">
            <QrCode className="w-6 h-6 text-slate-400" />
            <p className="font-semibold text-slate-700">Scan this with WhatsApp on your phone</p>
            <p className="text-xs text-slate-500 max-w-xs">WhatsApp app → Settings → Linked Devices → Link a Device</p>
            <img src={status.qr} alt="WhatsApp QR code" className="w-64 h-64 border border-slate-200 rounded-xl p-2 bg-white" />
            <p className="text-xs text-slate-400">Refreshes automatically if it expires</p>

            <div className="w-full max-w-xs border-t border-slate-100 pt-4 mt-1 space-y-2">
              <p className="text-sm font-semibold text-slate-700">Or link with a phone number</p>
              <form onSubmit={handleRequestPairingCode} className="flex gap-2">
                <input
                  type="tel" className="input-field" placeholder="Phone number (with country code)"
                  value={pairNumber} onChange={e => setPairNumber(e.target.value)}
                />
                <button type="submit" disabled={isRequestingCode} className="btn-primary px-4 shrink-0 disabled:opacity-50">
                  {isRequestingCode ? '...' : 'Get code'}
                </button>
              </form>
              {pairingCode && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <p className="text-2xl font-black tracking-widest text-emerald-700">{pairingCode}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    On that phone: WhatsApp → Settings → Linked Devices → Link a Device → "Link with phone number instead", then type this code.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center text-slate-500 py-6 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {status.status === 'connecting' ? 'Connecting to WhatsApp...' : 'Waiting for a QR code...'}
          </div>
        )}
      </div>

      {status?.status === 'connected' && (
        <div className="glass-panel p-6 space-y-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4" /> Default Report Group
          </h3>
          <p className="text-sm text-slate-500 -mt-1">
            {defaultGroup ? <>Currently set to <span className="font-semibold text-slate-700">{defaultGroup.name.trim()}</span>.</> : 'No default group set yet - pick one below.'}
          </p>
          {isLoadingGroups ? (
            <div className="text-center text-slate-500 py-4 flex items-center justify-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading groups...
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {(groups || []).map(g => {
                const isDefault = defaultGroup?.id === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => handleSetDefaultGroup(g)}
                    disabled={savingGroupId === g.id}
                    className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl border text-left text-sm transition-colors ${
                      isDefault ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="font-medium text-slate-700">{g.name.trim()} <span className="text-slate-400 font-normal">({g.participantCount})</span></span>
                    {isDefault && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
                  </button>
                );
              })}
              {groups && groups.length === 0 && <p className="text-sm text-slate-400 text-center py-3">No groups found on this account.</p>}
            </div>
          )}
        </div>
      )}

      {status?.status === 'connected' && (
        <form onSubmit={handleSaveAlertNumber} className="glass-panel p-6 space-y-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Phone className="w-4 h-4" /> Owner Alert Number
          </h3>
          <p className="text-sm text-slate-500 -mt-1">A personal number for higher-detail alerts, like the Daily Statement reconciliation.</p>
          <div className="flex gap-2">
            <input
              type="tel" className="input-field" placeholder="10-digit phone number"
              value={alertNumber} onChange={e => setAlertNumber(e.target.value)}
            />
            <button type="submit" disabled={isSavingAlertNumber} className="btn-primary px-4 disabled:opacity-50 shrink-0">
              {isSavingAlertNumber ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {status?.status === 'connected' && (
        <form onSubmit={handleSendTest} className="glass-panel p-6 space-y-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Send className="w-4 h-4" /> Send a Test Message
          </h3>
          <input
            type="tel" className="input-field" placeholder="10-digit phone number"
            value={testForm.number} onChange={e => setTestForm({ ...testForm, number: e.target.value })}
          />
          <textarea
            rows="3" className="input-field" placeholder="Message text"
            value={testForm.message} onChange={e => setTestForm({ ...testForm, message: e.target.value })}
          />
          <button type="submit" disabled={isSending} className="btn-primary flex items-center gap-2 px-4 py-2.5 disabled:opacity-50">
            <Send className="w-4 h-4" /> {isSending ? 'Sending...' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
};

export default WhatsAppSetup;
